import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Modal,
  ModalActions,
  MultiSelect,
  Page,
  PageHeader,
  Select,
  Table,
  type Action,
} from '@evogard/ui';
import type { Department, DepartmentPerson } from '@evogard/contract';
import { loadFailureMessage } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { DepartmentLabelsModal } from './DepartmentLabelsModal.js';

/**
 * Departamentos — o setor da casa, e a fila de quem atende demanda.
 *
 * ## Por que esta tela nasce vazia, como a de categorias
 *
 * Pelo mesmo motivo, e é a regra 14: a lista real está no banco de produção do
 * `ticketgard`, não no repositório dele. Semear "Financeiro, TI, Suporte" aqui
 * seria inventar o roteamento de uma casa que ninguém mediu — e roteamento
 * errado não é uma lista feia numa tela, é a demanda chegando à fila de quem não
 * a resolve. Sai por `tools/extract-ticketgard.sql` (bloco 1), em
 * `demandas-31`, antes de o sistema antigo ser desligado.
 *
 * ## O que a tela precisa denunciar, e denuncia
 *
 * **Departamento sem ninguém.** Uma categoria apontada para um setor vazio
 * roteia o aviso de demanda nova para lista nenhuma. O `memberCount` vem na
 * listagem por isso — e a linha diz, em vez de mostrar um zero silencioso.
 *
 * ## Quem entra no setor se define aqui também, desde 30/09/2026
 *
 * Até então só pela tela de Usuários, pessoa a pessoa — montar um setor de
 * oito era abrir oito modais. Agora criar o departamento abre em seguida o
 * modal das pessoas dele, e a linha ganhou "Adicionar pessoas". As duas telas
 * gravam a mesma tabela (`app.user_departments`), cada uma pelo seu lado.
 */
export function DepartmentsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const canEdit = can(context, 'platform.departments', 'editar');
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  /*
   * O setor cujas pessoas estão sendo editadas, e se ele acabou de nascer — o
   * que muda só o texto do modal: logo depois de criar, "Agora não" é a saída
   * natural, e não "cancelar" algo que ninguém começou.
   */
  const [peopleOf, setPeopleOf] = useState<{ dept: Department; fresh: boolean } | null>(
    null,
  );
  /*
   * O erro de arquivar mora na TELA, e não na linha.
   *
   * O `ActionsModal` fecha antes de a ação rodar — é construção dele —, então
   * não há onde mostrar a recusa dentro do painel. Ela aparece aqui em cima, que
   * é o mesmo lugar em que a mesa de demandas mostra a recusa de aderir.
   */
  const [actionError, setActionError] = useState<string | null>(null);
  /** O setor cujas etiquetas estão abertas (07/10/2026). */
  const [labelsOf, setLabelsOf] = useState<Department | null>(null);

  const query = useQuery({
    queryKey: ['plataforma', 'departamentos', tenantId, showArchived],
    queryFn: async () => {
      const r = await api.platform.listDepartments({
        query: showArchived ? { includeArchived: 'true' as const } : {},
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os departamentos'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const toggle = useMutation({
    mutationFn: async ({ dept, archived }: { dept: Department; archived: boolean }) => {
      const r = await api.platform.archiveDepartment({
        params: { id: dept.id },
        body: { archived },
      });
      return ok(r, 200, 'Não foi possível arquivar o departamento.');
    },
    onMutate: () => setActionError(null),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'departamentos'] });
      void client.invalidateQueries({ queryKey: ['demandas'] });
    },
    onError: (e: Error) => setActionError(e.message),
  });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Departamentos"
        actions={
          /* Esconder é cortesia; quem protege é o guard. A rota continua
             exigindo `platform.departments:editar`. */
          canEdit && (
            <Button icon="mais" onClick={() => setCreating(true)}>
              Novo departamento
            </Button>
          )
        }
      >
        O setor que atende cada categoria de ticket. Quem está no departamento e pode
        responder ticket recebe o aviso quando um chega — é o que substitui o &ldquo;quem
        viu, viu&rdquo;.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {actionError && <Alert tone="erro">{actionError}</Alert>}

      {creating && canEdit && (
        <DepartmentForm
          toClose={() => setCreating(false)}
          onCreated={(dept) => setPeopleOf({ dept, fresh: true })}
        />
      )}

      {peopleOf && canEdit && (
        <DepartmentMembersModal
          department={peopleOf.dept}
          fresh={peopleOf.fresh}
          onClose={() => setPeopleOf(null)}
        />
      )}

      {/* Erro sem dado nenhum: o aviso acima é a resposta inteira. A tabela
          anunciaria "Nenhum departamento" logo abaixo, e essa é a afirmação que
          parece um fato medido. */}
      {(!query.isError || query.data) && (
        <Card
          title="Departamentos"
          actions={
            <label className="flex items-center gap-2 text-xs text-texto-suave">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="size-4 rounded border-borda-forte accent-primaria-texto"
              />
              Mostrar arquivados
            </label>
          }
          bleed
        >
          <Table
            label="Departamentos da base e quantas pessoas há em cada um"
            items={query.data ?? []}
            loading={query.isLoading}
            rowKey={(d) => d.id}
            empty={
              'Nenhum departamento cadastrado nesta base. Sem departamento, o aviso de ' +
              'ticket novo vai para todo mundo que pode responder — a lista real vem do ' +
              'sistema atual e é cadastrada aqui.'
            }
            rowTitle={(d) => d.nome}
            rowSubtitle={(d) =>
              d.memberCount === 0
                ? 'Ninguém neste setor'
                : d.memberCount === 1
                  ? '1 pessoa'
                  : `${d.memberCount} pessoas`
            }
            rowIcon="pessoas"
            /*
             * Lista vazia para quem não pode editar, e não uma condicional em
             * volta: a tabela não desenha gatilho nenhum quando as ações somem,
             * em vez de abrir um painel vazio. O docblock de `rowActions` diz.
             */
            rowActions={(d): Action[] => {
              if (!canEdit) return [];
              const archived = Boolean(d.archivedAt);
              return [
                {
                  label: 'Adicionar pessoas',
                  icon: 'pessoas',
                  /*
                   * Aberto também no arquivado: dali ainda dá para TIRAR quem
                   * está. O modal só oferece os membros atuais nesse caso, e o
                   * serviço recusa gente nova do mesmo jeito.
                   */
                  run: () => setPeopleOf({ dept: d, fresh: false }),
                },
                {
                  label: 'Etiquetas',
                  icon: 'etiqueta',
                  /* A lista que quem é do setor põe nos cards (07/10/2026). */
                  run: () => setLabelsOf(d),
                },
                {
                  label: 'Editar',
                  icon: 'etiqueta',
                  /*
                   * O motivo escrito, e não o botão sumindo: renomear um setor
                   * arquivado grava um nome que nenhuma tela mostra. Antes disto
                   * o botão vinha `disabled` sem dizer por quê, que é o defeito
                   * que o `disabled` do `Action` existe para fechar.
                   */
                  disabled: archived
                    ? 'O departamento está arquivado. Reative antes de editar.'
                    : (false as const),
                  form: (close) => (
                    <EditForm
                      department={d}
                      canGrant={can(context, 'platform.users', 'editar')}
                      onDone={close}
                    />
                  ),
                },
                {
                  label: archived ? 'Reativar' : 'Arquivar',
                  icon: archived ? 'atualizar' : 'bloqueado',
                  /*
                   * `neutro` e não `perigo`: arquivar **não** tira ninguém do
                   * departamento — reativar um setor desfeito por engano devolve
                   * a equipe inteira. O que ele faz é parar de receber demanda
                   * nova. `perigo` é para o que não se desfaz.
                   */
                  run: () => toggle.mutate({ dept: d, archived: !archived }),
                },
              ];
            }}
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (d) => (
                  <span className={d.archivedAt ? 'text-texto-suave' : undefined}>
                    {d.nome}
                    {d.archivedAt && (
                      <Badge tone="neutro" className="ml-2">
                        Arquivado
                      </Badge>
                    )}
                    {d.descricao && (
                      <span className="mt-0.5 block text-xs text-texto-suave">
                        {d.descricao}
                      </span>
                    )}
                    {d.leaderGroup && (
                      <span className="mt-0.5 block text-xs text-texto-suave">
                        {d.leaderGroup.archived
                          ? `O grupo do líder, “${d.leaderGroup.nome}”, está arquivado e não concede nada.`
                          : `Quem lidera recebe também o grupo “${d.leaderGroup.nome}”.`}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                title: 'Pessoas',
                cell: (d) =>
                  /*
                   * Zero é dito com palavra, não com o algarismo.
                   *
                   * "0" numa coluna de números lê-se como um dado qualquer; o
                   * que a operação precisa notar é que aquele setor **não avisa
                   * ninguém**. É o mesmo critério de "Sem prazo" na tela de
                   * categorias: o estado que muda o comportamento é escrito.
                   */
                  d.memberCount === 0 ? (
                    <span className="text-atencao">Ninguém</span>
                  ) : (
                    <span className="tabular-nums">
                      {d.memberCount === 1 ? '1 pessoa' : `${d.memberCount} pessoas`}
                    </span>
                  ),
              },
            ]}
          />
        </Card>
      )}

      {labelsOf && (
        <DepartmentLabelsModal department={labelsOf} onClose={() => setLabelsOf(null)} />
      )}

      {query.data && query.data.some((d) => !d.archivedAt && d.memberCount === 0) && (
        <Alert tone="alerta" title="Há departamento sem ninguém">
          Categoria apontada para um departamento vazio faz o aviso de ticket novo cair
          para todo mundo que pode responder — ninguém fica sem saber, mas o recorte por
          setor deixa de valer. Quem entra em cada um se define aqui, em{' '}
          <strong className="font-medium">Adicionar pessoas</strong> na linha do
          departamento, ou na tela de <strong className="font-medium">Usuários</strong>.
        </Alert>
      )}
    </Page>
  );
}

/**
 * Novo departamento — os campos empilhados, como o de abrir demanda.
 *
 * Eram dois campos num `flex-wrap items-end` com largura escrita à mão em cada
 * um (`min-w-56 flex-1`), e o botão "Criar" como terceiro item da mesma linha.
 * É o defeito medido em 06/09 na barra de filtros: largura fixa em contêiner
 * elástico deixa um vão à direita que muda de tamanho a cada quebra — e foi por
 * ele que a barra virou uma grade e, em 17/09, o painel de `Filters`.
 * Empilhado em largura cheia não há vão nenhum.
 */
function DepartmentForm({
  toClose,
  onCreated,
}: {
  toClose: () => void;
  /** O setor recém-criado — a tela abre em seguida o modal das pessoas dele. */
  onCreated: (dept: Department) => void;
}) {
  const client = useQueryClient();
  const [nome, setName] = useState('');
  const [descricao, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const r = await api.platform.createDepartment({
        body: {
          nome: nome.trim(),
          descricao: descricao.trim() === '' ? null : descricao.trim(),
        },
      });
      return ok(r, 201, 'Não foi possível criar o departamento.');
    },
    onSuccess: (created) => {
      setName('');
      setDescription('');
      void client.invalidateQueries({ queryKey: ['plataforma', 'departamentos'] });
      toClose();
      onCreated(created);
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Card title="Novo departamento">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <Field
          label="Nome"
          required
          maxLength={120}
          value={nome}
          onChange={(e) => setName(e.target.value)}
          placeholder="Como a operação chama o setor"
        />

        <Field
          label="Descrição"
          maxLength={500}
          value={descricao}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="—"
          help="O que este setor atende"
        />

        <div className="flex gap-2">
          <Button type="submit" loading={create.isPending} disabled={!nome.trim()}>
            Criar
          </Button>
          <Button type="button" variant="contorno" onClick={toClose}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Renomear, dentro do painel de ações.
 *
 * Era um formulário NA CÉLULA, e saiu em 18/09 com o desenho de 17/09: a coluna
 * de ações virou reticências, e uma linha de tabela que se transforma em
 * formulário empurra as vizinhas para baixo e muda a largura das colunas
 * enquanto a pessoa digita. O painel é o mesmo lugar, sem o salto.
 *
 * A descrição viaja junto e não muda: `editDepartment` grava os dois campos, e
 * mandar `descricao` ausente apagaria o texto de quem só quis trocar o nome.
 *
 * O grupo do líder (29/10/2026) vai só quando muda: ausente, a API não mexe
 * nele, e renomear continua não exigindo a permissão de conceder acesso.
 */
function EditForm({
  department,
  canGrant,
  onDone,
}: {
  department: Department;
  /** `platform.users:editar` — escolher o grupo do líder é conceder acesso */
  canGrant: boolean;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const [nome, setName] = useState(department.nome);
  const current = department.leaderGroup?.id ?? '';
  const [leaderGroupId, setLeaderGroupId] = useState(current);
  const [error, setError] = useState<string | null>(null);

  const groups = useQuery({
    queryKey: ['plataforma', 'grupos', context?.currentTenant.id],
    enabled: canGrant,
    queryFn: async () => {
      const r = await api.platform.listGroups();
      return ok(r, 200, 'Não foi possível carregar os grupos.');
    },
  });
  const changedLeader = leaderGroupId !== current;

  const rename = useMutation({
    mutationFn: async () => {
      const r = await api.platform.editDepartment({
        params: { id: department.id },
        body: {
          nome: nome.trim(),
          descricao: department.descricao,
          /* ausente não mexe: renomear não pode exigir a permissão de conceder */
          ...(changedLeader ? { leaderGroupId: leaderGroupId === '' ? null : leaderGroupId } : {}),
        },
      });
      return ok(r, 200, 'Não foi possível salvar o departamento.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'departamentos'] });
      /* a lista de usuários mostra o nome do setor de cada pessoa, e as
       * categorias de demandas mostram o do setor que as atende: renomear
       * alcança as duas */
      void client.invalidateQueries({ queryKey: ['plataforma', 'usuarios'] });
      void client.invalidateQueries({ queryKey: ['demandas'] });
      onDone();
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (nome.trim() === department.nome && !changedLeader) return onDone();
        rename.mutate();
      }}
    >
      <Field
        label="Nome"
        required
        autoFocus
        maxLength={120}
        value={nome}
        onChange={(e) => setName(e.target.value)}
        error={error ?? undefined}
      />

      {/*
       * O grupo do líder (29/10/2026) — quem lidera o setor recebe as
       * permissões dele, somadas às do próprio grupo. Só quem pode dar grupo a
       * alguém (`platform.users:editar`) escolhe; para os demais, o texto diz
       * o que está valendo, e a API recusa do mesmo jeito.
       */}
      {canGrant ? (
        <Select
          label="Grupo do líder"
          value={leaderGroupId}
          onChange={(e) => setLeaderGroupId(e.target.value)}
          disabled={groups.isLoading || groups.isError}
          help="Quem for marcado como líder deste setor recebe as permissões deste grupo, além das do próprio grupo."
        >
          <option value="">Nenhum — liderar não concede acesso</option>
          {(groups.data ?? [])
            .filter((g) => !g.archivedAt || g.id === current)
            .map((g) => (
              <option key={g.id} value={g.id}>
                {g.nome}
                {g.archivedAt ? ' (arquivado)' : ''}
              </option>
            ))}
        </Select>
      ) : (
        <p className="text-sm text-texto-suave">
          {department.leaderGroup
            ? `Quem lidera recebe também o grupo “${department.leaderGroup.nome}”. Trocar exige a permissão de editar usuários.`
            : 'Liderar este setor não concede acesso. Escolher um grupo do líder exige a permissão de editar usuários.'}
        </p>
      )}
      {groups.isError && <Alert tone="erro">{groups.error.message}</Alert>}

      <div className="flex gap-2">
        <Button type="submit" loading={rename.isPending} disabled={!nome.trim()}>
          Salvar
        </Button>
        <Button type="button" variant="contorno" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/**
 * As pessoas do departamento — num modal, como a troca de departamentos da tela
 * de Usuários, e visto do outro lado da mesma tabela.
 *
 * ## Abre sozinho depois de criar
 *
 * Setor recém-criado está vazio, e vazio ele não avisa ninguém: é o estado que
 * a tela denuncia logo abaixo. Abrir o modal na sequência é o caminho comum;
 * "Agora não" deixa para depois, e o aviso de setor vazio continua lá.
 *
 * ## Manda o estado final, como `setUserDepartments`
 *
 * O rascunho é um mapa pessoa → lidera, pelo mesmo motivo escrito no modal de
 * Usuários: duas listas em paralelo deixariam a liderança órfã ao desmarcar, e
 * ela voltaria sozinha ao remarcar.
 *
 * ## A liderança só vale com a permissão do grupo
 *
 * Liderar é a marca aqui **e** `tickets.tickets:liderar` no grupo da pessoa. A
 * lista desta rota não carrega o grupo de cada um, então a frase fica escrita
 * em vez de calculada — é o aviso que o modal de Usuários mostra por pessoa.
 */
function DepartmentMembersModal({
  department,
  fresh,
  onClose,
}: {
  department: Department;
  fresh: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const archived = Boolean(department.archivedAt);
  /* `null` até a primeira mexida: antes disso o rascunho é o que o banco diz */
  const [chosen, setChosen] = useState<Map<string, boolean> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const people = useQuery({
    queryKey: ['plataforma', 'departamentos', 'pessoas', department.id],
    queryFn: async () => {
      const r = await api.platform.listDepartmentMembers({
        params: { id: department.id },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as pessoas da base'));
      }
      return r.body;
    },
  });

  const draft =
    chosen ??
    new Map(
      (people.data ?? []).filter((p) => p.member).map((p) => [p.id, p.leads] as const),
    );

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.platform.setDepartmentMembers({
        params: { id: department.id },
        body: {
          members: [...draft].map(([userId, leads]) => ({ userId, leads })),
        },
      });
      return ok(r, 200, 'Não foi possível trocar as pessoas do departamento.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['plataforma', 'departamentos'] });
      /* a coluna "Departamentos" da tela de Usuários muda junto */
      void client.invalidateQueries({ queryKey: ['plataforma', 'usuarios'] });
      onClose();
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Arquivado não recebe gente nova, e o seletor não a oferece: só quem já
   * está aparece, para poder sair. Oferecer os outros seria a tela propondo o
   * que o serviço vai recusar.
   */
  const offered: DepartmentPerson[] = (people.data ?? []).filter(
    (p) => !archived || p.member,
  );
  const byId = new Map(offered.map((p) => [p.id, p]));
  const marked = [...draft.keys()]
    .map((id) => byId.get(id))
    .filter((p): p is DepartmentPerson => p !== undefined)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  /* desmarcar APAGA a liderança junto — ver o docblock do modal de Usuários */
  const pick = (ids: string[]) =>
    setChosen(new Map(ids.map((id) => [id, draft.get(id) === true])));

  const setLeads = (id: string, leads: boolean) => {
    const next = new Map(draft);
    next.set(id, leads);
    setChosen(next);
  };

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      tone="destaque"
      size="lg"
      icon="pessoas"
      title={fresh ? 'Quem entra no departamento' : 'Pessoas do departamento'}
      descricao={department.nome}
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}
        {people.isError && <Alert tone="erro">{people.error.message}</Alert>}

        {fresh && (
          <p className="text-sm text-texto-suave">
            O departamento foi criado e ainda não tem ninguém. Quem entrar nele e puder
            responder ticket recebe o aviso quando chegar um da categoria dele.
          </p>
        )}

        {archived && (
          <Alert tone="alerta">
            Este departamento está arquivado e não recebe gente nova. Dá para tirar quem
            ainda está nele; para pôr alguém, reative antes.
          </Alert>
        )}

        {!people.isError && (
          <div className="flex flex-col gap-4">
            <MultiSelect
              label="Pessoas"
              placeholder="Ninguém"
              searchPlaceholder="Buscar pessoa…"
              emptyMessage={
                archived
                  ? 'Ninguém está neste setor.'
                  : 'Ninguém com esse nome nesta base.'
              }
              layer="modal"
              loading={people.isLoading}
              options={offered.map((p) => ({
                value: p.id,
                label: p.blocked ? `${p.nome} (bloqueada)` : p.nome,
              }))}
              selected={[...draft.keys()]}
              onChange={pick}
            />

            {/*
             * A liderança só existe DENTRO do vínculo, e por isso aparece só para
             * quem está marcado — mesmo critério do modal de Usuários.
             */}
            {marked.length > 0 && (
              <fieldset className="flex flex-col gap-1">
                <legend className="mb-1 text-xs font-medium text-texto">Liderança</legend>
                {marked.map((p) => (
                  <label
                    key={p.id}
                    className="flex min-h-11 items-center gap-3 text-sm text-texto"
                  >
                    <input
                      type="checkbox"
                      checked={draft.get(p.id) === true}
                      onChange={(e) => setLeads(p.id, e.target.checked)}
                      className="size-5 rounded border-borda-forte accent-primaria-texto"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">Lidera — {p.nome}</span>
                      <span className="truncate text-xs text-texto-suave">{p.email}</span>
                    </span>
                    {p.blocked && (
                      <Badge tone="neutro" className="ml-auto">
                        Bloqueada
                      </Badge>
                    )}
                  </label>
                ))}
                {[...draft.values()].some(Boolean) && (
                  <p className="text-xs text-texto-suave">
                    {department.leaderGroup && !department.leaderGroup.archived
                      ? `Quem lidera recebe também o grupo “${department.leaderGroup.nome}”, além do próprio grupo.`
                      : 'A liderança só vale para quem tem “Agir como líder” no grupo.'}
                  </p>
                )}
              </fieldset>
            )}
          </div>
        )}

        <ModalActions className="border-t border-borda-suave pt-5">
          <Button
            type="submit"
            loading={save.isPending}
            disabled={people.isLoading || people.isError}
          >
            Salvar
          </Button>
          <Button type="button" variant="contorno" onClick={onClose}>
            {fresh ? 'Agora não' : 'Cancelar'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
